import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import {
  deleteService,
  getService,
  patchService,
  postService,
} from "../services";

function normalizeShopList(inner) {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner.data)) return inner.data;
  return (
    inner.locations ??
    inner.shops ??
    inner.records ??
    inner.list ??
    []
  );
}

export const fetchShops = createAsyncThunk(
  "shop/fetchShops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.location, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      const inner = envelope.data ?? envelope;
      return normalizeShopList(inner);
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const createShop = createAsyncThunk(
  "shop/createShop",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postService(api_urls.location, payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const updateShop = createAsyncThunk(
  "shop/updateShop",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await patchService(api_urls.locationById(id), payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return { id, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const deleteShopById = createAsyncThunk(
  "shop/deleteShopById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteService(api_urls.locationById(id));
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return { id, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  saving: false,
  deleting: false,
};

export const shopSlice = createSlice({
  name: "shop",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchShops.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchShops.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchShops.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createShop.pending, (state) => {
        state.saving = true;
      })
      .addCase(createShop.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createShop.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateShop.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateShop.fulfilled, (state, { payload }) => {
        state.saving = false;
        if (payload?.id != null && payload?.data) {
          const merged = payload.data;
          state.list = state.list.map((s) =>
            String(s.id) === String(payload.id) ? { ...s, ...merged } : s
          );
        }
      })
      .addCase(updateShop.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteShopById.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteShopById.fulfilled, (state, { payload }) => {
        state.deleting = false;
        if (payload?.id != null) {
          state.list = state.list.filter(
            (s) => String(s.id) !== String(payload.id)
          );
        }
      })
      .addCase(deleteShopById.rejected, (state) => {
        state.deleting = false;
      });
  },
});

export default shopSlice.reducer;
